import {
	AuthService,
	Container,
	Service,
	TrackingEventNames,
	TrackingService,
} from '../../../../symphony';

import {
	CloseAISolutionsClickSource,
	GetSolutionClickSource,
	ImpressionSource,
	SolutionClickType,
} from '../../global-interface';
import {
	SlAIChatContext,
	SlAISolutionsContext,
	SolutionActionType,
	SolutionContext,
	aIChatPagePath,
	aILandingPagePath,
} from '../../global-constants';
import { SlDataService } from '../sl-data.service';
import { MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

@Service()
export class SlTrackingService {
	private aISolutionSupportItemTypes = {
		3: 'HINT',
		1: 'EXPLAIN THE CODE',
		2: 'FIND THE BUG',
		5: 'CC SOLUTION',
	};

	private aISolutionLocation = {
		[MaterialType.CodeCoach]: 'CODE COACH',
		[MaterialType.CodeProject]: 'CODE PROJECT',
		[MaterialType.CodeRepo]: 'CODE REPO',
		[MaterialType.TryItYourself]: 'TIY',
	};

	private trackingService = Container.take('global', TrackingService);

	private aIChatDataService = Container.take(SlAIChatContext, SlDataService);

	private authService: AuthService = Container.take('global', AuthService);

	private aISolutionsDataService = Container.take(
		SlAISolutionsContext,
		SlDataService,
	);

	private getImpressionSource = () => {
		const pathname = window.location.pathname;

		if (pathname === '/') {
			return ImpressionSource.Homepage;
		}

		if (pathname === aIChatPagePath) {
			return ImpressionSource.Kodie;
		}

		if (pathname === aILandingPagePath) {
			return ImpressionSource.LandingPage;
		}

		if (pathname.split('/')[1] === 'profile') {
			return ImpressionSource.Profile;
		}

		return ImpressionSource.Other;
	};

	private getMaterialRelationIds = () => ({
		dynamicId: (this.aISolutionsDataService.materialHeader$.value?.dynamicId || '').toString(),
		staticId: (this.aISolutionsDataService.materialHeader$.value?.staticId || '').toString(),
		materialId: (this.aISolutionsDataService.materialHeader$.value?.materialId || '').toString(),
	});

	private getClickType = (actionId: SolutionActionType) => {
		let clickType: SolutionClickType;
		switch (actionId) {
			case SolutionActionType.ExplainCode:
				clickType = SolutionClickType.ExplainTheCode;
				break;
			case SolutionActionType.FindBug:
				clickType = SolutionClickType.FindTheBug;
				break;
			case SolutionActionType.Hint:
				clickType = SolutionClickType.Hint;
				break;
			case SolutionActionType.GetSolution:
				clickType = SolutionClickType.CCSolution;
				break;
			case SolutionActionType.AlternativeLessonExplanation:
				clickType = SolutionClickType.AlternativeExplanation;
				break;
			case SolutionActionType.QuestionHint:
				clickType = SolutionClickType.QuizHint;
				break;
			case SolutionActionType.RealLifeExamples:
				clickType = SolutionClickType.RealLifeExamples;
				break;

			default:
				break;
		}

		return clickType;
	};

	private getKodieLocationInLesson = () => {
		if (this.aISolutionsDataService.questionData$.value) {
			return 'QUIZ';
		}
		return 'THEORY';
	};

	public trackAIChatMount = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.solotutorImpression,
			eventParams: {
				source: this.getImpressionSource(),
				location: window.location.href,
				referer: document.referrer,
			},
		});
	};

	public trackSendMessage = () => {
		const conversationId = this.aIChatDataService.conversationId$.value;
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.solotutorSendMessage,
			...(conversationId && {
				eventParams: {
					conversationId:
						this.aIChatDataService.conversationId$.value,
				},
			}),
		});
	};

	public trackCourseClick = (alias: string) => {
		const conversationId = this.aIChatDataService.conversationId$.value;
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.solotutorCourseLinkClick,
			eventParams: {
				conversationId,
				courseAlias: alias,
			},
		});
	};

	public trackSolutionRateClick = (
		id: string,
		solutionRating: number,
		solutionType: number,
		context: SolutionContext,
	) => {
		if (context === SolutionContext.Practice) {
			this.trackSolutionRateClickInPractice(
				id,
				solutionRating,
				solutionType,
			);
		}

		if (context === SolutionContext.Lesson) {
			this.trackSolutionRateClickInLesson(
				id,
				solutionRating,
				solutionType,
			);
		}
	};

	private trackSolutionRateClickInPractice = (
		id: string,
		solutionRating: number,
		solutionType: number,
	) => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodiePlaygroundFeedback,
			eventParams: {
				supportItemType: this.aISolutionSupportItemTypes[solutionType],
				location:
					this.aISolutionLocation[
					this.aISolutionsDataService.aISolutionsMaterialType$
						.value
					],
				feedbackType: solutionRating === 1 ? 'POSITIVE' : 'NEGATIVE',
				isPro: false,
				responseId: id,
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackSolutionRateClickInLesson = (
		id: string,
		solutionRating: number,
		solutionType: number,
	) => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodieLessonFeedback,
			eventParams: {
				supportItemType: this.getClickType(solutionType),
				location: this.getKodieLocationInLesson(),
				feedbackType: solutionRating === 1 ? 'POSITIVE' : 'NEGATIVE',
				isPro: false,
				responseId: id,
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	private trackGetSolutionTraAgainInPractice = (
		getSolutionClickSource: GetSolutionClickSource,
		actionId: SolutionActionType,
	) => {
		const clickType = this.getClickType(actionId);

		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodiePlaygroundClick,
			eventParams: {
				location:
					this.aISolutionLocation[
					this.aISolutionsDataService.aISolutionsMaterialType$
						.value
					],
				isPro: false,
				eventType: getSolutionClickSource,
				isResponseAvailable:
					getSolutionClickSource !== GetSolutionClickSource.TryAgain,
				clickType,
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackGetSolutionClick = (
		getSolutionClickSource: GetSolutionClickSource,
		actionId: SolutionActionType,
		context: SolutionContext,
	) => {
		if (
			getSolutionClickSource !== GetSolutionClickSource.AskAgain
			&& getSolutionClickSource !== GetSolutionClickSource.TryAgain
		) {
			return;
		}

		if (context === SolutionContext.Practice) {
			this.trackGetSolutionTraAgainInPractice(
				getSolutionClickSource,
				actionId,
			);
		}

		if (context === SolutionContext.Lesson) {
			this.trackGetSolutionTryAgainInLesson(actionId);
		}
	};

	public trackCloseAISolutionsClick = (
		actionId: SolutionActionType,
		closeAISolutionsClickSource: CloseAISolutionsClickSource,
	) => {
		if (
			closeAISolutionsClickSource
			!== CloseAISolutionsClickSource.BackToCode
		) {
			return;
		}

		const clickType = this.getClickType(actionId);

		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodiePlaygroundClick,
			eventParams: {
				location:
					this.aISolutionLocation[
					this.aISolutionsDataService.aISolutionsMaterialType$
						.value
					],
				isPro: false,
				eventType: CloseAISolutionsClickSource.BackToCode,
				isResponseAvailable: false,
				clickType,
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackAISolutionCollapse = (
		actionId: SolutionActionType,
		isExpanding: boolean,
		context: SolutionContext,
	) => {
		if (context === SolutionContext.Practice) {
			this.trackKodiInPracticeSolutionCollapse(actionId, isExpanding);
		}

		if (context === SolutionContext.Lesson) {
			this.trackKodiInLessonNavigationSolutionCollapse(
				actionId,
				isExpanding,
			);
		}
	};

	public trackAISolutionsOpenClick = () => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodiePlaygroundClick,
			eventParams: {
				location:
					this.aISolutionLocation[
					this.aISolutionsDataService.aISolutionsMaterialType$
						.value
					],
				isPro: false,
				eventType: 'EXPAND',
				isResponseAvailable: false,
				clickType: 'ANIMATION',
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackAnswerExplanationCTAClick = () => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodieLessonClick,
			eventParams: {
				clickType: 'BOTTOM SHEET CTA',
				location: this.getKodieLocationInLesson(),
				isPro: false,
				isCorrect:
					!!this.aISolutionsDataService.answerExplanationData$.value
						?.isCorrect,
				eventType: 'CTA_CLICK',
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackKodiInLessonNavigationIconClick = () => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodieLessonClick,
			eventParams: {
				clickType: 'NAVIGATION BAR ICON',
				location: this.getKodieLocationInLesson(),
				isPro: false,
				eventType: 'ICON_CLICK',
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackGetSolutionTryAgainInLesson = (
		actionId: SolutionActionType,
	) => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodieLessonClick,
			eventParams: {
				clickType: this.getClickType(actionId),
				location: this.getKodieLocationInLesson(),
				isPro: false,
				eventType: 'ASK_AGAIN',
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	public trackAISolutionResponseCopy = (actionId: SolutionActionType) => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodieLessonClick,
			eventParams: {
				clickType: this.getClickType(actionId),
				location: this.getKodieLocationInLesson(),
				isPro: false,
				eventType: 'COPY_TEXT',
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	private trackKodiInLessonNavigationSolutionCollapse = (
		actionId: SolutionActionType,
		isExpanding: boolean,
	) => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodieLessonClick,
			eventParams: {
				clickType: this.getClickType(actionId),
				location: this.getKodieLocationInLesson(),
				isPro: false,
				eventType: isExpanding ? 'EXPAND' : 'COLLAPSE',
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};

	private trackKodiInPracticeSolutionCollapse = (
		actionId: SolutionActionType,
		isExpanding: boolean,
	) => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.kodiePlaygroundClick,
			eventParams: {
				location:
					this.aISolutionLocation[
					this.aISolutionsDataService.aISolutionsMaterialType$
						.value
					],
				isPro: false,
				eventType: isExpanding ? 'EXPAND' : 'COLLAPSE',
				isResponseAvailable:
					!!this.aISolutionsDataService.aiSolutionsData$.value[
					actionId
					],
				clickType: this.getClickType(actionId),
				version: '2-0-0',
				...this.getMaterialRelationIds(),
			},
		});
	};
}
